import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { HelpCircle, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const faqs = [
  {
    q: "What ages do you teach?",
    a: "Our programs are designed for children ages 6 to 14. We have age-appropriate courses starting from visual block coding for younger children to Python programming for older students.",
  },
  {
    q: "Does my child need prior coding experience?",
    a: "Not at all! Our Intro to Coding course is designed for complete beginners. We start with the basics and gradually build skills at each child's pace.",
  },
  {
    q: "How large are the class sizes?",
    a: "We keep our classes small with a maximum of 8 students per instructor. This ensures every child gets personalized attention and support.",
  },
  {
    q: "What equipment does my child need?",
    a: "For most coding courses, we provide all necessary equipment including laptops and tablets. For robotics classes, all kits and materials are included in the program fee.",
  },
  {
    q: "Do you offer a free trial class?",
    a: "Yes! We offer a complimentary trial class so your child can experience our teaching style and environment before committing. Contact us to schedule your free session.",
  },
  {
    q: "How do I join classes?",
    a: "We offer both online classes and resources accessible from anywhere in the world. All you need is a computer and internet connection to join our programs.",
  },
  {
    q: "What is your refund policy?",
    a: "We offer a full refund within the first two weeks of enrollment if you or your child are not satisfied with the program. No questions asked.",
  },
];

export const FaqSection = () => {
  return (
    <section data-testid="faq-section" className="pt-[35px] pb-[35px] md:py-24 bg-[#FBFBF7]">
      <div className="mx-auto w-full max-w-[var(--container-max)] px-4 sm:px-6">
        {/* Section start */}

        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 lg:gap-16">
          {/* Left: Help card (sticky) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="lg:sticky lg:top-24 lg:self-start"
          >
            <div className="bg-white rounded-2xl border border-border p-6 shadow-[var(--shadow-soft)]">
              <div className="w-12 h-12 rounded-xl bg-[#EAF2FF] flex items-center justify-center mb-4">
                <HelpCircle className="w-6 h-6 text-[#1677FF]" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3">
                Frequently Asked{" "}
                <span className="text-[#1677FF]">Questions</span>
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                Can't find what you're looking for? We're happy to help!
              </p>
              <Link to="/contact">
                <Button className="rounded-full bg-[#1677FF] hover:bg-[#0F63DA] text-white btn-lift w-full">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Contact Us
                </Button>
              </Link>
            </div>
          </motion.div>

          {/* Right: Accordion */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            <Accordion type="single" collapsible className="space-y-3">
              {faqs.map((faq, i) => (
                <AccordionItem
                  key={i}
                  value={`faq-${i}`}
                  className="bg-white rounded-xl border border-border px-5 data-[state=open]:shadow-[var(--shadow-soft)]"
                  data-testid={`faq-item-${i}`}
                >
                  <AccordionTrigger className="text-sm font-semibold text-left hover:no-underline py-4">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground leading-relaxed pb-4">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default FaqSection;
